import { PriceRangeDisplay } from 'components/PriceDisplay'
import { useTokens } from 'hooks/Tokens'
import { useProcessedPoolData } from 'hooks/useProcessedPoolData'
import { useMemo } from 'react'
import { Bound } from 'state/mint/v3/actions'
import styled from 'styled-components'
import { formatTickPriceForRangeDisplay } from 'utils/formatTickPrice'

interface PortfolioRangeDisplayProps {
  token0Address?: string | null
  token1Address?: string | null
  lowerTick?: number | null
  upperTick?: number | null
  currentTick?: number | null
  feeTier?: number | null
  provider?: string
  poolType?: 'Island' | 'V2' | 'V3' | 'Charm' | 'Generic'
  emptyClassName?: string
  showLabelsOnMobile?: boolean
}

export const PortfolioRangeDisplay = ({
  token0Address,
  token1Address,
  lowerTick,
  upperTick,
  currentTick,
  feeTier,
  provider,
  poolType = 'V3',
  emptyClassName = 'text-kodiak-text-secondary text-xs',
  showLabelsOnMobile = false,
}: PortfolioRangeDisplayProps) => {
  const [token0, token1] = useTokens([token0Address, token1Address])

  const fallbackData = useMemo(
    () => ({
      lowerTick: lowerTick ?? undefined,
      upperTick: upperTick ?? undefined,
      currentTick: currentTick ?? undefined,
      feeTier: feeTier ?? undefined,
      poolType,
    }),
    [currentTick, feeTier, lowerTick, poolType, upperTick]
  )

  const processedData = useProcessedPoolData(undefined, token0, token1, fallbackData)

  const formattedPrices = useMemo(() => {
    if (!processedData) return { lower: null, upper: null }

    return {
      lower: formatTickPriceForRangeDisplay(
        processedData.priceLower,
        processedData.isLowerPriceAtLimit,
        Bound.LOWER,
        processedData.feeTier
      ),
      upper: formatTickPriceForRangeDisplay(
        processedData.priceUpper,
        processedData.isUpperPriceAtLimit,
        Bound.UPPER,
        processedData.feeTier
      ),
    }
  }, [processedData])

  if (!token0Address || !token1Address) {
    return <div className={emptyClassName}>—</div>
  }

  if (!processedData) {
    return <div className={emptyClassName}>—</div>
  }

  if (poolType !== 'V2' && provider !== 'charm' && (!processedData.priceLower || !processedData.priceUpper)) {
    return <div className={emptyClassName}>—</div>
  }

  return (
    <MobileRangeWrapper $showLabelsOnMobile={showLabelsOnMobile}>
      <PriceRangeDisplay
        provider={provider}
        currencyBase={processedData.currencyBase}
        currencyQuote={processedData.currencyQuote}
        priceLower={processedData.priceLower}
        priceUpper={processedData.priceUpper}
        formattedPriceLower={formattedPrices.lower}
        formattedPriceUpper={formattedPrices.upper}
      />
    </MobileRangeWrapper>
  )
}

const MobileRangeWrapper = styled.div<{ $showLabelsOnMobile?: boolean }>`
  ${({ theme, $showLabelsOnMobile }) =>
    $showLabelsOnMobile &&
    theme.mediaWidth.upToSmall`
      display: inline-block;
      width: max-content;

      & > div {
        width: max-content;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        row-gap: 2px;
      }

      & > div > div {
        width: max-content;
        display: grid;
        grid-template-columns: 32px max-content;
        align-items: center;
        column-gap: 6px;
        padding: 1px 0 !important;
      }

      & > div > div > span:first-child {
        display: inline-block !important;
        width: 32px;
        min-width: 32px;
        text-align: left;
        margin-right: 0;
      }

      & > div > div:first-child > div:last-child {
        margin-left: 0 !important;
      }

      & > div > div > div:last-child {
        width: max-content;
        display: grid !important;
        grid-template-columns: 14px max-content 14px max-content;
        align-items: center;
        column-gap: 3px;
      }
    `};
`
